import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { map } from 'lodash';

import BlockSection from '~/components/wrappers/BlockSection';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import ButtonGroup from '~/components/base/ButtonGroup';
import ArrowCta from '~/components/base/ArrowCta';

import CarWashSearch from '~/components/CarWashSearch';

import { formatText } from '~/utilities/helpers';

import { h2 } from '~/styles/typography';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes,
    AcfLink,
    AcfBackgroundColor
} from '~/utilities/propTypes';

const Wrapper = styled.div`
    color: ${({ theme }) => theme.colors.primary.white};
    display: ${({ hideMobile }) => (hideMobile ? 'none' : 'block')};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: block;
    }
`;

const Title = styled.h2`
    ${h2};
    text-align: center;
    margin-bottom: ${({ theme }) => theme.layout.gutter};
`;

export const SubTitle = styled.h3`
    ${h2};
    text-align: center;
    margin-bottom: ${({ theme }) => theme.layout.gutter};
`;

const CtaBar = ({
    variant = 'singleColumn',
    backgroundColor = 'blue',
    title = '',
    leftTitle = '',
    rightTitle = '',
    ctas = [],
    displayMobileSearch = false,
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => (
    <Wrapper hideMobile={variant === 'findYourCarWash' && !displayMobileSearch}>
        <BlockSection
            {...{ backgroundColor, globalBlockOptions, attributes }}
            padding="medium"
        >
            {title ? (
                <Row>
                    <Column span={8} start={3}>
                        <Title>{formatText(title)}</Title>
                    </Column>
                </Row>
            ) : null}

            {ctas?.length ? (
                variant === 'findYourCarWash' ? (
                    <CarWashSearch {...{ leftTitle, rightTitle, ctas }} />
                ) : variant === 'threeColumn' ? (
                    <Row justify="flex-start" rowGap="gutter">
                        {ctas.map(
                            ({
                                copy,
                                link,
                                useComparisonTable,
                                comparisonTableProductCategory
                            }) => (
                                <Column
                                    span={12}
                                    spanTablet={4}
                                    key={`${link.title}--${link.url}`}
                                >
                                    <ArrowCta
                                        {...{ copy, link, useComparisonTable }}
                                        comparisonTableProductCategory={
                                            comparisonTableProductCategory?.id
                                        }
                                    />
                                </Column>
                            )
                        )}
                    </Row>
                ) : variant === 'addWash' ? (
                    <Row justify="center">
                        <Column span={12}>
                            <ButtonGroup
                                links={map(ctas, ({ link }) => ({
                                    ...link,
                                    addWashButton: true
                                }))}
                            />
                        </Column>
                    </Row>
                ) : (
                    <Row justify="center">
                        <Column span={12}>
                            <ButtonGroup
                                links={map(
                                    ctas,
                                    ({
                                        link,
                                        useComparisonTable,
                                        comparisonTableProductCategory
                                    }) => ({
                                        ...link,
                                        useComparisonTable,
                                        comparisonTableProductCategory:
                                            comparisonTableProductCategory?.id
                                    })
                                )}
                            />
                        </Column>
                    </Row>
                )
            ) : null}
        </BlockSection>
    </Wrapper>
);

CtaBar.propTypes = {
    variant: PropTypes.string,
    ...AcfBackgroundColor,
    title: PropTypes.string,
    leftTitle: PropTypes.string,
    rightTitle: PropTypes.string,
    ctas: PropTypes.arrayOf(
        PropTypes.shape({
            copy: PropTypes.string,
            link: AcfLink,
            useComparisonTable: PropTypes.bool,
            comparisonTableProductCategory: PropTypes.shape({
                id: PropTypes.string
            })
        })
    ),
    displayMobileSearch: PropTypes.bool,
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default CtaBar;
